import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { cx } from "cva"
import type * as React from "react"
import type { Row } from "../../../generated/schema.ts"
import * as queries from "../../../shared/queries.ts"
import { Menu } from "../../components/Menu.tsx"
import * as Auth from "../../lib/auth.ts"

export function Layout(
  props: React.PropsWithChildren<{
    direction: "horizontal" | "vertical"
  }>,
) {
  const { children, direction } = props
  return (
    <div
      className={cx(
        "flex h-full bg-inset",
        direction === "vertical" ? "flex-col" : undefined,
      )}
    >
      {children}
    </div>
  )
}

export namespace Layout {
  export function Empty(props: React.PropsWithChildren<{ title: string }>) {
    const { title, children } = props

    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-8 px-4 pt-16">
        <div className="flex w-full max-w-[50ch] flex-col items-center gap-2 text-center">
          <h1 className="font-bold text-2xl">{title}</h1>
          <p className="text-muted">{children}</p>
        </div>
      </div>
    )
  }

  export function Content(
    props: React.PropsWithChildren<{ hide?: boolean | undefined }>,
  ) {
    const { children, hide = false } = props
    return (
      <div
        className={cx(
          "flex min-w-0 flex-1 flex-col bg-default",
          hide && "hidden",
        )}
      >
        {children}
      </div>
    )
  }
  export function StartSidebar(
    props: React.PropsWithChildren<{
      visibility: "visible" | "hidden"
    }>,
  ) {
    const { children, visibility } = props
    // TODO: drag changes width
    return (
      <div
        className={cx(
          "flex h-full w-start-sidebar flex-col border-neutral-6 border-e bg-inset",
          visibility === "visible" &&
            "w-start-sidebar max-w-start-sidebar max-md:fixed max-md:inset-0 max-md:z-10 max-md:w-full max-md:border-none",
          visibility === "hidden" && "max-md:hidden",
        )}
      >
        {children}
      </div>
    )
  }

  export function EndSidebar(
    props: React.PropsWithChildren<{
      visibility: "visible" | "maximized" | "hidden"
    }>,
  ) {
    const { children, visibility } = props
    // TODO: drag changes width
    return (
      <div
        className={cx(
          "flex min-w-0 flex-col border-neutral-6 border-l bg-default max-md:fixed max-md:inset-0 max-md:z-10 max-md:w-full max-md:border-none",
          visibility === "maximized" && "flex-1 border-none",
          (visibility === "visible" || visibility === "maximized") &&
            "w-end-sidebar max-w-end-sidebar",
          visibility === "hidden" && "hidden",
        )}
      >
        {children}
      </div>
    )
  }

  export function Breadcrumb(props: Breadcrumb.Props) {
    const { repository, repositoryOwner } = props

    const { data: authData } = Auth.useAuthData()
    const [repositories] = useQuery(
      queries
        .repositoriesForAccount(authData, {
          id: repositoryOwner.id,
          type: repositoryOwner.type,
        })
        .orderBy("name", "asc"),
    )
    const activeRepositoryId = repository?.id

    return (
      <div className="flex items-center gap-2">
        <img
          alt={repositoryOwner?.login}
          className="size-7 select-none rounded-md text-transparent shadow-px"
          src={repositoryOwner?.avatar_url}
        />

        <div />

        {repositories.length ? (
          <Menu>
            <Menu.Button
              className={cx(
                "-mx-2 -my-1 rounded px-2 py-1 text-[14px] hover:bg-control-transparent-hover",
                !repository && "font-bold",
              )}
            >
              {repositoryOwner?.login}
            </Menu.Button>
            <Menu.Menu>
              <Menu.Group>
                <Menu.GroupLabel>Repositories</Menu.GroupLabel>
                {repositories.map((repository) => (
                  <Menu.Item
                    active={activeRepositoryId === repository.id}
                    key={repository.id}
                    render={
                      <Link
                        className="flex items-center gap-1"
                        params={{
                          owner: repositoryOwner.login,
                          repo: repository.name,
                        }}
                        to="/$owner/$repo"
                      >
                        {repository.name}
                        {repository.private && (
                          <OcticonLock16 className="size-3" />
                        )}
                      </Link>
                    }
                  />
                ))}
              </Menu.Group>
            </Menu.Menu>
          </Menu>
        ) : (
          <Link
            className="-mx-2 -my-1 flex items-center gap-1 rounded px-2 py-1 font-bold text-[14px] hover:bg-control-transparent-hover"
            params={{
              owner: repositoryOwner?.login!,
            }}
            to="/$owner"
          >
            {repositoryOwner?.login}
          </Link>
        )}

        {repository && (
          <>
            <div className="text-muted">
              <svg
                aria-hidden="true"
                height="16"
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.956 1.27994L6.06418 14.7201L5 14.7201L9.89181 1.27994L10.956 1.27994Z"
                  fill="currentcolor"
                />
              </svg>
            </div>
            <Link
              className="-mx-2 -my-1 flex items-center gap-1 rounded px-2 py-1 font-bold text-[14px] hover:bg-control-transparent-hover"
              params={{
                owner: repositoryOwner?.login!,
                repo: repository?.name!,
              }}
              to="/$owner/$repo"
            >
              {repository?.name}
              {repository.private && <OcticonLock16 className="size-3" />}
            </Link>
          </>
        )}
      </div>
    )
  }

  export namespace Breadcrumb {
    export type Props = {
      repository?: Row.repository | undefined
      repositoryOwner: Pick<Row.account, "avatar_url" | "id" | "login"> & {
        type: "account" | "organization"
      }
    }
  }

  export function Header(
    props: React.PropsWithChildren<{ showLogo?: boolean }>,
  ) {
    const { children, showLogo = true } = props
    const { data: authData } = Auth.useAuthData()
    const [account] = useQuery(queries.me(authData, {}), {
      enabled: Boolean(authData?.id),
    })

    return (
      <div className="flex min-h-header items-center justify-between border-neutral-6 border-b bg-inset px-4 dark:border-neutral-7">
        <div className="flex w-full items-center gap-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              {showLogo && <span className="font-semibold">GitChat</span>}
              {children}
            </div>

            <div className="flex items-center gap-4">
              {account && (
                <Menu>
                  <Menu.Button>
                    <img
                      alt={account.login}
                      className="size-8 rounded-full text-transparent shadow-px"
                      src={account.avatar_url}
                    />
                  </Menu.Button>
                  <Menu.Menu>
                    <Menu.Item
                      icon={OcticonSignOut16}
                      onClick={() => Auth.logout()}
                    >
                      Sign out
                    </Menu.Item>
                  </Menu.Menu>
                </Menu>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}
